import React from 'react';
import { Box, Typography } from '@mui/material';

interface TennisScoreDisplayProps {
  scores: string;
  isPlayer1: boolean;
}

export function TennisScoreDisplay({ scores, isPlayer1 }: TennisScoreDisplayProps) {
  if (!scores) return null;

  // Handle both comma-separated and space-separated formats
  const normalizedScores = scores.includes(',') ? scores.split(',') : scores.trim().split(/\s+/);
  const scoreParts = normalizedScores.map((s) => s.trim()).filter(Boolean);

  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      {[0, 1, 2].map((index) => {
        const part = scoreParts[index] || '-';
        // Example part: "6(7-5)" or "7" or "6-7(5-7)"
        const tbMatch = part.match(/\((.*?)\)/);
        let gameScore = part.replace(/\(.*?\)/, '').trim();
        let tiebreakPoints = '';

        // Extract individual game score for this player
        if (gameScore.includes('-')) {
          const games = gameScore.split('-');
          gameScore = isPlayer1 ? games[0].trim() : games[1].trim();
        }

        if (tbMatch) {
          const tbValue = tbMatch[1]; // e.g. "7-5"
          const tbParts = tbValue.split('-');
          if (tbParts.length === 2) {
            // Distribute: Player 1 gets the first, Player 2 gets the second
            tiebreakPoints = isPlayer1 ? tbParts[0].trim() : tbParts[1].trim();
          } else {
            // Fallback for single number tiebreak e.g. "6(5)"
            tiebreakPoints = tbValue;
          }
        }

        // Color logic:
        const scoreColor = 'text.primary';

        return (
          <Box
            key={index}
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 20,
              height: 20,
              bgcolor: 'background.subtle',
              borderRadius: 0.5,
              border: '1px solid',
              borderColor: 'divider',
              px: 0.5,
              opacity: part === '-' ? 0.3 : 1, // Dim placeholder hyphens
            }}
          >
            <Typography
              component="span"
              sx={{
                fontSize: '0.75rem',
                fontWeight: 700,
                color: scoreColor,
                display: 'flex',
                alignItems: 'baseline',
              }}
            >
              {gameScore}
              {tiebreakPoints && (
                <Box
                  component="span"
                  sx={{
                    fontSize: '0.6em',
                    fontWeight: 800,
                    ml: 0.1,
                    verticalAlign: 'super',
                    lineHeight: 1,
                    alignSelf: 'flex-start',
                    mt: 0.2,
                  }}
                >
                  {tiebreakPoints}
                </Box>
              )}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}
